import React, { useContext, useEffect } from 'react'
import { AppSettings } from '../../config/app-settings'
import {
  Card,
  CardBody,
  CardFooter,
  CardHeader
} from '../../components/other/card/card'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import { DatePicker, Select } from 'antd'
import Loader from '../pages/loader'
import { toast } from 'react-toastify'
import { can } from '../../helpers/auth'
import NoPermission from '../pages/noPermission'
import dayjs from 'dayjs'
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter'
import { useUpdateVideoDatesMutation } from '../../redux/Api/VideoApiSlice'
dayjs.extend(isSameOrAfter)


function removeNullValues(obj) {
  return Object.fromEntries(
    Object.entries(obj).filter(([_, value]) => value !== null)
  );
}


function ChangeLinksDate () {
  const [updateVideoDates, { isLoading, data: success, error }] = useUpdateVideoDatesMutation()

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      url: '',
      link_type: '',
      report_date: null,
      remove_date: null,
      monetized_date: null,
      retracted_date: null
    },
    validationSchema: Yup.object()
      .shape({
        url: Yup.string().required('Please enter URLs'),
        link_type: Yup.string().required('Please select a links status'),

        report_date: Yup.string().nullable(),
        remove_date: Yup.string().nullable(),

        monetized_date: Yup.string()
          .nullable()
          .when('link_type', {
            is: 'monetized',
            then: schema => schema.required('Please select monetized date')
          }),

        retracted_date: Yup.string()
          .nullable()
          .when('link_type', {
            is: 'retracted',
            then: schema => schema.required('Please select retracted date')
          })
      })
      .test(
        'reported-dates-required',
        'Please select at least one of Reported Date or Removed Date',
        function (values) {
          if (values.link_type !== 'reported') return true
          return !!values.report_date || !!values.remove_date
        }
      )
      .test(
        'remove-after-report',
        'Removed date must be the same or after Reported date',
        function (values) {
          if (
            values.link_type !== 'reported' ||
            !values.report_date ||
            !values.remove_date
          )
            return true
          return dayjs(values.remove_date).isSameOrAfter(
            dayjs(values.report_date)
          )
        }
      ),

    onSubmit: async (values, { resetForm }) => {
      if (values['link_type'] === 'reported') {
        if (
          (values['remove_date'] === null) &
          (values['report_date'] === null)
        ) {
          toast.error('Please Select one date at least.')
          return
        }
        if (values.report_date && values.remove_date) {

          if(!dayjs(values.remove_date).isSameOrAfter(
            dayjs(values.report_date)
          )){
            toast.error('Remove date most be greater or equal report date.')
          return
          }
        }
      }
      const urls = values['url']
        .split('\n')
        .map(url => url.trim())
        .filter(url => url !== '')

      let details = {
        link_type: values['link_type'],
        report_date: values['report_date'],
        remove_date: values['remove_date'],
        monetized_date: values['monetized_date'],
        retracted_date: values['retracted_date']
      }

      if (values['link_type'] === 'reported') {
        delete details.monetized_date
        delete details.retracted_date
      } else if (values['link_type'] === 'monetized') {
        delete details.remove_date
        delete details.report_date
        delete details.retracted_date
      } else if (values['link_type'] === 'retracted') {
        delete details.remove_date
        delete details.monetized_date
        delete details.report_date
      }

      details = removeNullValues(details)
      let body = urls.map(x => {
        return {
          url: x,
          ...details
        }
      })
      if (Object.keys(validation.errors).length > 0) {
        Object.keys(validation.errors).forEach(error => {
          toast.error(validation.errors[error])
        })
      }

      if (Object.keys(validation.errors).length === 0) {
        try {
          await updateVideoDates(body).unwrap()
          body = {}
          resetForm()
        } catch (err) {
          // Error handled by useEffect
        }
      }
    }
  })
  

  useEffect(() => {
    if (error?.data) {
      const errorMessages = Object.values(error.data).flat()
      errorMessages.forEach(message => {
        toast.error(message)
      })
    }
  }, [error])

  useEffect(() => {
    if (success) {
      toast.success('Urls date updated successfully.')
    }
  }, [success])

  const context = useContext(AppSettings)
  useEffect(() => {
    context.setAppHeaderNone(false)
    context.setAppSidebarNone(false)
    context.setAppContentClass('')
    context.setAppThemePanal(true)

    return function cleanUp () {
      context.setAppHeaderNone(true)
      context.setAppSidebarNone(true)
      context.setAppContentClass('p-0')
      context.setAppThemePanal(false)
    }
  }, [])

  if (!can('change_videourl_dates')) {
    return <NoPermission />
  }
  

  return (
    <div>
      <Loader isLoading={isLoading} />
      <Card>
        <CardHeader>Change Links Date</CardHeader>
        <CardBody>
          <div className='mb-3'>
            <label htmlFor='url'>URLs (separated by new lines)</label>
            <br />
            <textarea
              name='url'
              value={validation.values.url}
              onChange={validation.handleChange}
              onBlur={validation.handleBlur}
              placeholder='Enter url'
              style={{
                height: '250px',
                width: '100%',
                backgroundColor: 'transparent',
                border: '1px solid white',
                padding: '8px'
              }}
            />
            {validation.touched.url && validation.errors.url && (
              <p className='text-danger'>{validation.errors.url}</p>
            )}
          </div>

          <div className='mb-3'>
            <label htmlFor='link_type'>Report Status</label>
            <br />
            <Select
              className='customSelect'
              name='link_type'
              style={{
                width: '100%'
              }}
              defaultValue={validation.values.link_type}
              value={validation.values.link_type}
              onChange={e => validation.setFieldValue('link_type', e)}
              onBlur={validation.handleBlur}
              options={[
                { label: 'Reported / Removed', value: 'reported' },
                { label: 'Retracted', value: 'retracted' },
                { label: 'Monetized', value: 'monetized' }
              ]}
            />
            {validation.touched.link_type && validation.errors.link_type && (
              <p className='text-danger'>{validation.errors.link_type}</p>
            )}
          </div>

          {validation.values.link_type === 'reported' && (
            <>
              <div className='customeDate mb-3'>
                <label>Reported Date</label>
                <DatePicker
                  style={{ width: '100%' }}
                  name='report_date'
                  onChange={(e, date) =>
                    validation.setFieldValue('report_date', date)
                  }
                  onBlur={validation.handleBlur}
                  value={
                    validation.values.report_date
                      ? dayjs(validation.values.report_date)
                      : null
                  }
                />
                {validation.touched.report_date &&
                  validation.errors.report_date && (
                    <p className='text-danger'>
                      {validation.errors.report_date}
                    </p>
                  )}
              </div>
              <div className='customeDate mb-3'>
                <label>Removed Date</label>
                <DatePicker
                  style={{ width: '100%' }}
                  name='remove_date'
                  onChange={(e, date) =>
                    validation.setFieldValue('remove_date', date)
                  }
                  onBlur={validation.handleBlur}
                  value={
                    validation.values.remove_date
                      ? dayjs(validation.values.remove_date)
                      : null
                  }
                />
                {validation.touched.remove_date &&
                  validation.errors.remove_date && (
                    <p className='text-danger'>
                      {validation.errors.remove_date}
                    </p>
                  )}
              </div>
            </>
          )}

          {validation.values.link_type === 'monetized' && (
            <div className='customeDate mb-3'>
              <label>Monetized Date</label>
              <DatePicker
                style={{ width: '100%' }}
                name='monetized_date'
                onChange={(e, date) =>
                  validation.setFieldValue('monetized_date', date)
                }
                onBlur={validation.handleBlur}
                value={
                  validation.values.monetized_date
                    ? dayjs(validation.values.monetized_date)
                    : null
                }
              />
              {validation.touched.monetized_date &&
                validation.errors.monetized_date && (
                  <p className='text-danger'>
                    {validation.errors.monetized_date}
                  </p>
                )}
            </div>
          )}

          {validation.values.link_type === 'retracted' && (
            <div className='customeDate mb-3'>
              <label>Retracted Date</label>
              <DatePicker
                style={{ width: '100%' }}
                name='retracted_date'
                onChange={(e, date) =>
                  validation.setFieldValue('retracted_date', date)
                }
                onBlur={validation.handleBlur}
                value={
                  validation.values.retracted_date
                    ? dayjs(validation.values.retracted_date)
                    : null
                }
              />
              {validation.touched.retracted_date &&
                validation.errors.retracted_date && (
                  <p className='text-danger'>
                    {validation.errors.retracted_date}
                  </p>
                )}
            </div>
          )}
        </CardBody>
        <CardFooter>
          <div className='flex gap-4'>
            <button
              type='submit'
              className='btn btn-outline-theme'
              onClick={e => validation.handleSubmit()}
              disabled={isLoading}
            >
              Submit
            </button>
          </div>
        </CardFooter>
      </Card>
    </div>
  )
}

export default ChangeLinksDate
